// Angular
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
// RxJS
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, timeout } from 'rxjs/operators';
// NGRX
import { select, Store } from '@ngrx/store';
// Auth reducers and selectors
import { AppState } from '../../../core/reducers/';
import { UserRequested } from '../_actions/auth.actions';
import { isUserLoaded } from '../_selectors/auth.selectors';
import { AuthorizationService } from 'app/core/ana/authorization/_services/authorization.service';
import { ChangePasswordService } from '../password-governance/_services/change-password.service';
import { environment } from 'environments/environment';
import { LayoutUtilsService } from 'app/core/_base/crud';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class AuthGuard implements CanActivate {
    constructor(
        private store: Store<AppState>,
        private router: Router,
        private authService: AuthorizationService,
        private changePasswordService: ChangePasswordService,
        private layoutUtilsService: LayoutUtilsService,
        private translateService: TranslateService
    ) { }

    /**
     * Resolves username: from localStorage, or by waiting for user load when token exists (e.g. return from Duo).
     */
    private resolveUsername(state: RouterStateSnapshot): Observable<string | null> {
        let username = localStorage.getItem('user');
        if (username) {
            return of(username);
        }
        if (state.url.includes('auth/reset-password')) {
            return of(null);
        }
        const token = localStorage.getItem(environment.authTokenKey);
        if (token) {
            this.store.dispatch(new UserRequested());
            return this.store.pipe(
                select(isUserLoaded),
                filter(loaded => loaded === true),
                take(1),
                timeout(10000),
                map(() => localStorage.getItem('user')),
                catchError(() => of(null))
            );
        }
        return of(null);
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.resolveUsername(state).pipe(
            switchMap(username => {
                if (!username && !state.url.includes('auth/reset-password')) {
                    this.router.navigateByUrl('/auth/login');
                    return of(false);
                }
                if (state.url.includes('auth/reset-password')) {
                    const userEncoded = state.url.split('user=')[1];
                    return this.changePasswordService.validateLink(userEncoded).pipe(
                        tap(auth => {
                            if (!auth) {
                                const modalRef = this.layoutUtilsService.generalAlert();
                                modalRef.componentInstance.data = this.translateService.instant('AUTH.CHANGE_PASSWORD.EXPIRED_LINK');
                                this.router.navigateByUrl('/auth/login');
                            }
                        })
                    );
                }
                // Standard auth procedure
                const angularURL = state.url.split('/');
                let componentAsParam = angularURL.length > 3 ? '/' + angularURL[2] + '/' + angularURL[3] : '/' + angularURL[2];
                if (componentAsParam.includes('/processing/createEditComplaints')) {
                    componentAsParam = '/processing/createEditComplaints';
                }
                if (componentAsParam.includes('?')) {
                    componentAsParam = componentAsParam.split('?')[0];
                }
                const applicationAsParam = environment.host + angularURL[1];
                return this.authService.verifyClosedSession().pipe(
                    switchMap(status =>
                        this.authService.isAuthorized(username, componentAsParam, applicationAsParam).pipe(
                            tap(auth => {
                                if (status) {
                                    const modalRef = this.layoutUtilsService.generalAlert();
                                    modalRef.componentInstance.hasWarning = true;
                                    modalRef.componentInstance.data = this.translateService.instant('AUTH.GENERAL.SESION_EXPIRED');
                                    this.router.navigateByUrl('/auth/login');
                                } else if (!auth) {
                                    if (componentAsParam === '/dashboard') {
                                        this.router.navigateByUrl('/home');
                                    } else {
                                        this.router.navigateByUrl('/error/403');
                                    }
                                }
                            })
                        )
                    )
                );
            })
        );
    }
}
